import { DotLegalButton, DotLegalLink, DotLegalTooltip, useIsOnExtraSmallScreen, useIsOnSmallScreen } from "@dotlegal/dotlegal-ui-components";
import { Box, Button, Divider, IconButton } from "@mui/material";
import React from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import NotificationMenu from "../../notificationMenu/NotificationMenu.tsx";
import { changeOrganization, helpIcon, settingsIcon } from "../../../common/icons.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { Link, useNavigate } from "react-router";
import { useNavMenuDataMapping } from "../../NavMenu/NavMenu.hooks.ts";
import UserMenu from "./UserMenu.tsx";
import GlobalSearchField from "./GlobalSearchField.tsx";
import CreateNewMenuItem from "./CreateNewMenuItem.tsx";
import DotLegalChatIcon from "../../../common/svgs/dotlegal-chat-icon.svg?react";
import AiChatIcon from "../../../common/svgs/aiChatIcon.svg?react";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useHubSpotChat } from "./useHubSpotChat.ts";
import { useToggleAiChat } from "../../panes/usePanes";
import BookDemoButton from "@common/components/bookDemoButton/BookDemoButton.tsx";
import { hasSettingsAccess } from "../../../privacySettings/settings/SettingsRedirect.tsx";
import AiFeatureGate from "../../../plan/aiFeatureGate/AiFeatureGate";
import InboxIcon from "@common/svgs/inbox.svg?react";
import NotificationDotBadge from "@common/components/notificationDotBadge/NotificationDotBadge";
import { useObservationsContext } from "@common/providers/observationsProvider/ObservationsProvider";

function TopBar() {
    const navigate = useNavigate();
    const isOnExtraSmallScreen = useIsOnExtraSmallScreen();
    const isOnSmallScreen = useIsOnSmallScreen();

    const { helpLink, permissions, customerName } = useUserContext();
    const { translateString } = useTranslation();
    const { getHelpUrl, getChangeOrganizationUrl, getSettingsUrl, getObservationsUrl } = useUrlProvider();
    const { hasUnseenObservations } = useObservationsContext();
    const { showNotification, notifications, isLoading } = useNavMenuDataMapping();
    const { addonsFeatures, contractManagementFeatures } = usePlanContext();
    const { openChat, isLoading: hubspotTokenIsLoading, isAvailable } = useHubSpotChat();
    const toggleAiChat = useToggleAiChat();

    return (
        <Box
            sx={(theme) => ({
                minHeight: 50,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingRight: 2,
                paddingLeft: isOnSmallScreen ? 2 : 0,
                backgroundColor: theme.customPalette.white,
                paddingTop: 1,
                paddingBottom: 1,
                flexDirection: addonsFeatures?.research && !permissions.globalDocumentSearchAccess ? "row-reverse" : "row",
            })}
            className="top-bar"
        >
            {(!addonsFeatures.research || (addonsFeatures.research && permissions.globalDocumentSearchAccess)) && (
                <Box id={"top-bar-search"} sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
                    {isOnExtraSmallScreen ? undefined : <GlobalSearchField />}
                    <CreateNewMenuItem />
                </Box>
            )}

            <Box id={"top-bar-controls"} sx={{ display: "flex", gap: 1, alignItems: "center", "& svg": { fill: "#DAD9EF", height: 20, width: 20 } }}>
                <BookDemoButton showWithFreePlan buttonType="green" width={125} />

                <DotLegalTooltip text={translateString("help")}>
                    <Box id={"top-bar-help"} component={Link} target={"_blank"} rel={"noopener noreferrer"} to={getHelpUrl(helpLink)}>
                        <IconButton>{helpIcon}</IconButton>
                    </Box>
                </DotLegalTooltip>

                {isAvailable && !addonsFeatures.research && (
                    <DotLegalTooltip text={translateString("chatWithDotLegalTooltip")}>
                        <Box id={"top-bar-chat-with-us"} component={"span"}>
                            <IconButton onClick={openChat} disabled={hubspotTokenIsLoading}>
                                <DotLegalChatIcon />
                            </IconButton>
                        </Box>
                    </DotLegalTooltip>
                )}

                {hasSettingsAccess(permissions) ? (
                    <DotLegalTooltip text={translateString("settings")}>
                        <Box id={"top-bar-settings"} component={"span"}>
                            <DotLegalLink to={getSettingsUrl()}>
                                <IconButton>{settingsIcon}</IconButton>
                            </DotLegalLink>
                        </Box>
                    </DotLegalTooltip>
                ) : undefined}

                {permissions.observationPermissions.read && (
                    <DotLegalTooltip text={translateString("observations")}>
                        <Box id={"top-bar-observations"} component={"span"}>
                            <DotLegalLink to={getObservationsUrl()}>
                                <IconButton>
                                    <NotificationDotBadge show={hasUnseenObservations}>
                                        <InboxIcon />
                                    </NotificationDotBadge>
                                </IconButton>
                            </DotLegalLink>
                        </Box>
                    </DotLegalTooltip>
                )}

                {/* Tooltip is handled inside the menu, so we only wraps the icon with a tooltip and not the entire menu */}
                <NotificationMenu data={notifications} isLoading={isLoading} showNotificationIcon={showNotification} isExpanded />

                {!addonsFeatures.research && contractManagementFeatures.hasContractManagement && (
                    <AiFeatureGate>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Divider orientation={"vertical"} sx={{ minHeight: 32 }} />
                            <DotLegalTooltip text={translateString("aiComplianceChatTooltip")}>
                                <Box id={"top-bar-ai-chat"} component={"span"}>
                                    <Button
                                        onClick={addonsFeatures.aiFeatures ? toggleAiChat : undefined}
                                        startIcon={<AiChatIcon />}
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: 500,
                                            fontSize: 14,
                                            color: "#E127AA",
                                            padding: "6px 10px",
                                            "& .MuiButton-startIcon svg": { height: 22, width: 18, fill: "none" },
                                            "&:hover": { backgroundColor: "rgba(225, 39, 170, 0.08)" },
                                        }}
                                    >
                                        {translateString("aiComplianceChat")}
                                    </Button>
                                </Box>
                            </DotLegalTooltip>
                        </Box>
                    </AiFeatureGate>
                )}

                {localStorage.getItem("customerId") !== null ? (
                    <DotLegalTooltip text={translateString("changeOrganization")}>
                        {isOnSmallScreen ? (
                            changeOrganization
                        ) : (
                            <Box
                                id={"top-bar-change-org"}
                                sx={{ display: "flex", alignItems: "center", marginRight: 1, "& .MuiButton-root:hover": { textDecoration: "none" } }}
                            >
                                <Divider orientation={"vertical"} sx={{ minHeight: 32 }} />
                                <DotLegalButton
                                    buttonType={"linkButton"}
                                    onClick={() => navigate(getChangeOrganizationUrl())}
                                    id="change-organization"
                                    toolTip={""}
                                >
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", "& .MuiTypography-root": { lineHeight: 0 } }}>
                                        {changeOrganization}
                                        <Box>{customerName}</Box>
                                    </Box>
                                </DotLegalButton>
                                <Divider orientation={"vertical"} sx={{ minHeight: 32 }} />
                            </Box>
                        )}
                    </DotLegalTooltip>
                ) : undefined}

                {/* Tooltip is handled inside the menu, so we only wraps the icon with a tooltip and not the entire menu */}
                <UserMenu />
            </Box>
        </Box>
    );
}

export default TopBar;
